import { ChevronDown, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { User } from '../api/types';

interface PersonComboboxProps {
  label: string;
  nullLabel: string;
  users: User[];
  value: string;
  onChange: (value: string) => void;
  emptyValue?: string;
  unchangedOption?: { label: string; value: string };
}

export function PersonCombobox({
  label,
  nullLabel,
  users,
  value,
  onChange,
  emptyValue = '',
  unchangedOption,
}: PersonComboboxProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = users.find((user) => String(user.id) === value);
  const valueLabel = value === unchangedOption?.value
    ? unchangedOption.label
    : value === emptyValue
      ? ''
      : selected?.initial ?? '';
  const [query, setQuery] = useState(valueLabel);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) setQuery(valueLabel);
  }, [open, valueLabel]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    if (!needle || valueLabel === query) return users;
    return users.filter((user) =>
      `${user.initial} ${user.login}`.toLocaleLowerCase('ru').includes(needle),
    );
  }, [query, users, valueLabel]);

  const choose = (nextValue: string) => {
    onChange(nextValue);
    const nextUser = users.find((user) => String(user.id) === nextValue);
    setQuery(nextValue === unchangedOption?.value ? unchangedOption.label : nextUser?.initial ?? '');
    setOpen(false);
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  return (
    <label className="field state-inline-field state-person-field">
      <span>{label}</span>
      <div className={`person-combobox${open ? ' is-open' : ''}`}>
        <input
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open}
          autoComplete="off"
          onBlur={() => window.setTimeout(() => setOpen(false), 0)}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setQuery(nextQuery);
            setOpen(true);
            setActiveIndex(0);
            if (!nextQuery) onChange(emptyValue);
          }}
          onClick={() => setOpen(true)}
          onFocus={(event) => {
            setOpen(true);
            event.currentTarget.select();
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex((current) => Math.min(current + 1, filtered.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((current) => Math.max(current - 1, 0));
            } else if (event.key === 'Enter' && open && filtered[activeIndex]) {
              event.preventDefault();
              choose(String(filtered[activeIndex].id));
            } else if (event.key === 'Escape') {
              event.stopPropagation();
              setOpen(false);
              setQuery(valueLabel);
            }
          }}
          placeholder={nullLabel}
          ref={inputRef}
          role="combobox"
          value={query}
        />
        {value !== emptyValue && value !== unchangedOption?.value ? (
          <button aria-label={`Очистить: ${label}`} className="person-combobox__clear" onClick={() => choose(emptyValue)} type="button">
            <X size={13} />
          </button>
        ) : (
          <ChevronDown aria-hidden="true" className="person-combobox__chevron" size={14} />
        )}
        {open ? (
          <div className="person-combobox__menu" id={listId} role="listbox">
            {unchangedOption ? (
              <button
                aria-selected={value === unchangedOption.value}
                className={value === unchangedOption.value ? 'is-selected' : ''}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(unchangedOption.value);
                }}
                role="option"
                type="button"
              >
                {unchangedOption.label}
              </button>
            ) : null}
            <button
              aria-selected={value === emptyValue}
              className={value === emptyValue ? 'is-selected' : ''}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(emptyValue);
              }}
              role="option"
              type="button"
            >
              {nullLabel}
            </button>
            {filtered.map((user, index) => (
              <button
                aria-selected={String(user.id) === value}
                className={`${String(user.id) === value ? 'is-selected ' : ''}${index === activeIndex ? 'is-active' : ''}`.trim()}
                key={user.id}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(String(user.id));
                }}
                role="option"
                type="button"
              >
                {user.initial}
              </button>
            ))}
            {!filtered.length ? <span className="person-combobox__empty">Ничего не найдено</span> : null}
          </div>
        ) : null}
      </div>
    </label>
  );
}
